import React from 'react';
import {
  Brain, Target, TrendingUp, Zap, Eye,
  Globe, Shield, IndianRupee, Activity, BarChart3,
  Settings, LineChart,
} from 'lucide-react';
import ServicePage from '../../components/enterprise/ServicePage';

const AIAnalyticsPage = () => (
  <ServicePage
    hero={{
      eyebrow: 'AI Analytics',
      title: (
        <>
          Apply pre-trained models <em>to close the generation gap</em>
        </>
      ),
      lede: 'Apply pre-trained models to identify root causes of energy losses and bridge the gap between target and actual generation with our advanced AI-driven analytics platform.',
      image: '/assets/img/general/ai-generated-8685407_1280.jpg',
      imageAlt: 'AI-driven renewable energy analytics',
      actions: [
        { label: 'Request a walkthrough', to: '/contact' },
        { label: 'Explore all services', to: '/service' },
      ],
      metrics: [
        { value: '95%+', label: 'Prediction accuracy' },
        { value: '50%', label: 'Faster issue detection' },
        { value: '30%', label: 'Cost reduction' },
        { value: '24/7', label: 'AI monitoring' },
      ],
    }}
    overview={{
      eyebrow: 'Overview',
      title: 'Advanced AI features',
      lede: 'Our AI-driven analytics platform provides comprehensive capabilities to optimize your renewable energy operations and maximize performance.',
      body: [
        'Intelligent insights and predictions powered by advanced machine learning algorithms.',
      ],
      checklist: [
        'Advanced machine learning models for renewable energy optimization',
        'Real-time performance prediction and anomaly detection',
        'Automated root cause analysis for performance issues',
        'Predictive maintenance scheduling and optimization',
        'Energy generation forecasting and capacity planning',
        'Integration with existing SCADA and monitoring systems',
        'Custom AI model training for specific asset types',
        'Continuous learning and model improvement algorithms',
      ],
      image: '/assets/img/general/steptodown.com254434.jpg',
      imageAlt: 'Renewable assets feeding AI analytics models',
      imageCaption: 'Models trained on renewable energy data, not generic time series',
    }}
    capabilities={{
      eyebrow: 'Advanced AI analytics',
      title: 'Leverage cutting-edge artificial intelligence',
      lede: 'Optimize your renewable energy operations with purpose-built models.',
      items: [
        {
          icon: Brain,
          title: 'Machine learning models',
          description: 'Advanced AI algorithms trained on renewable energy data to predict performance and identify optimization opportunities.',
        },
        {
          icon: Target,
          title: 'Root cause analysis',
          description: 'Intelligent analysis to identify the underlying causes of performance issues and energy losses.',
        },
        {
          icon: TrendingUp,
          title: 'Performance optimization',
          description: 'AI-driven recommendations to optimize energy generation and improve overall asset efficiency.',
        },
        {
          icon: Eye,
          title: 'Predictive insights',
          description: 'Forecast future performance and maintenance needs using advanced predictive analytics.',
        },
      ],
    }}
    coverage={{
      eyebrow: 'Pre-trained AI models',
      title: 'Models built for renewable energy optimization',
      lede: 'Reported accuracy for each model currently in production.',
      items: [
        {
          icon: Activity,
          title: 'Performance prediction model',
          description: 'Predicts energy output based on weather, equipment status, and historical data.',
          meta: 'Accuracy 95.2%',
        },
        {
          icon: BarChart3,
          title: 'Anomaly detection AI',
          description: 'Identifies unusual patterns and potential issues in real-time.',
          meta: 'Accuracy 98.7%',
        },
        {
          icon: Settings,
          title: 'Maintenance optimization',
          description: 'Optimizes maintenance schedules to minimize downtime and costs.',
          meta: 'Accuracy 92.8%',
        },
        {
          icon: LineChart,
          title: 'Energy forecasting',
          description: 'Predicts energy generation for better grid integration and trading.',
          meta: 'Accuracy 94.5%',
        },
      ],
    }}
    outcomes={{
      eyebrow: 'Transform your operations',
      title: 'See measurable improvements in efficiency, reliability, and profitability',
      items: [
        {
          icon: Zap,
          title: 'Increased energy output',
          description: 'Optimize generation capacity and reduce energy losses through AI-driven insights.',
        },
        {
          icon: Shield,
          title: 'Reduced downtime',
          description: 'Predict and prevent equipment failures before they impact operations.',
        },
        {
          icon: IndianRupee,
          title: 'Cost optimization',
          description: 'Minimize operational costs and maximize ROI through intelligent resource allocation.',
        },
        {
          icon: Globe,
          title: 'Environmental impact',
          description: 'Reduce carbon footprint by optimizing renewable energy generation efficiency.',
        },
      ],
    }}
    cta={{
      title: 'Ready to harness the power of AI?',
      body: 'Transform your renewable energy operations with our advanced AI-driven analytics platform.',
      primary: { label: 'Talk to our team', to: '/contact' },
      secondary: { label: 'View all services', to: '/service' },
    }}
  />
);

export default AIAnalyticsPage;
